import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC, MouseEvent, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { buttonBaseStyle } from 'src/components/buttons-and-actions/button';
import type { DateType } from 'src/components/data-display/format-date';
import { FormatDate } from 'src/components/data-display/format-date';
import { Stack } from 'src/components/layout/stack';
import { Colors } from 'src/styles';

export const tagGap = 1.2;

export const tagBaseStyle = css`
  color: #fff;
  border: none;
  line-height: 1.8rem;
  font-size: 1.3rem;
  letter-spacing: 0.15rem;
  text-align: center;
  word-break: break-word;

  padding: 0 ${tagGap}rem;
  background-color: ${Colors.brandLight1};
  border-radius: 1rem;
`;

const Label = styled.button.attrs({ type: 'button' })`
  ${buttonBaseStyle};
  ${tagBaseStyle};

  padding: 0 0.6rem 0 ${tagGap}rem;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;

  &:hover,
  &:focus {
    background-color: ${Colors.brandLight2};
  }
`;

const RemoveButton = styled.button.attrs({ type: 'button' })`
  ${buttonBaseStyle};
  ${tagBaseStyle};

  padding: 0 ${tagGap}rem 0 0.6rem;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;

  &:hover,
  &:focus {
    background-color: ${Colors.brandSecondary};
  }
`;

const TagWrapper = styled.div`
  display: inline-block;
`;

const TagButton = styled.button.attrs({ type: 'button' })<{
  isRemove: boolean;
}>`
  ${tagBaseStyle}
  display: inline-block;

  &:hover,
  &:focus {
    background-color: ${Colors.brandLight2};
  }

  ${({ isRemove }) =>
    isRemove &&
    `
    &:hover,
    &:focus {
      background-color: ${Colors.brandSecondary};
    }
  `}
`;

const Capizalize = styled.span`
  text-transform: uppercase;
`;

export type TagProps = {
  label: ReactNode;
  value?: ReactNode;
  onClick?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  onRemove?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  testid?: string;
  /**
   * Be **very** careful with this prop as it can break the accessibility of the component.
   * Only do it if you provide an alternative way of achieving the same functionality via keyboard.
   */
  tabIndex?: number;
};

export const Tag: FC<TagProps> = ({
  onClick,
  onRemove,
  label,
  value,
  testid = 'tag-id',
  tabIndex,
}) => {
  const clickHandler = useCallback(
    (event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      if (onClick) {
        onClick(event);
      } else if (onRemove) {
        onRemove(event);
      }
    },
    [onClick, onRemove]
  );

  const removeHandler = useCallback(
    (event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      if (onRemove) {
        onRemove(event);
      } else if (onClick) {
        onClick(event);
      }
    },
    [onClick, onRemove]
  );

  const hasOnlyClick = useMemo(
    () => Boolean(onClick) && !onRemove,
    [onClick, onRemove]
  );

  const hasOnlyRemove = useMemo(
    () => Boolean(onRemove) && !onClick,
    [onClick, onRemove]
  );

  if (hasOnlyClick) {
    return (
      <TagButton
        data-testid={testid}
        isRemove={hasOnlyRemove}
        onClick={clickHandler}
        tabIndex={tabIndex}
      >
        <Capizalize>{label}</Capizalize>
        {value && <> · {value}</>}
      </TagButton>
    );
  }

  if (hasOnlyRemove) {
    return (
      <TagButton
        data-testid={testid}
        isRemove={hasOnlyRemove}
        onClick={clickHandler}
        tabIndex={tabIndex}
      >
        <Stack flow="column" gap={tagGap}>
          <span>
            <Capizalize>{label}</Capizalize>
            {value && <> · {value}</>}
          </span>
          <FontAwesomeIcon icon={faTimes} aria-label="Remove selection" />
        </Stack>
      </TagButton>
    );
  }

  return (
    <TagWrapper>
      <Stack flow="column" alignItems="stretch">
        <Label onClick={clickHandler} data-testid={testid} tabIndex={tabIndex}>
          <Capizalize>{label}</Capizalize>
          {value && <> · {value}</>}
        </Label>
        <RemoveButton
          onClick={removeHandler}
          data-testid={`remove-${testid}`}
          tabIndex={tabIndex}
        >
          <FontAwesomeIcon icon={faTimes} aria-label="Remove selection" />
        </RemoveButton>
      </Stack>
    </TagWrapper>
  );
};

type TagDateRangeProps = {
  label: ReactNode;
  from: string | null;
  to: string | null;
  type?: DateType;
  onClick?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  onRemove?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  testid?: string;
};

export const TagDateRange: FC<TagDateRangeProps> = ({
  from,
  to,
  type,
  label,
  onClick,
  onRemove,
  testid,
}) =>
  !from && !to ? null : (
    <Tag
      label={label}
      value={
        <>
          {from && to && (
            <>
              <FormatDate type={type} value={from} /> -{' '}
              <FormatDate type={type} value={to} />
            </>
          )}
          {from && !to && (
            <>
              from <FormatDate type={type} value={from} />
            </>
          )}
          {!from && to && (
            <>
              to <FormatDate type={type} value={to} />
            </>
          )}
        </>
      }
      onClick={onClick}
      onRemove={onRemove}
      testid={testid}
    />
  );

export const shortenTagText = (str: string | number, numberOfChars = 25) =>
  str.toString().replace(new RegExp(`(.{${numberOfChars}})..+`), '$1...');

type TagCollectionProps = {
  collection: string[];
  label: ReactNode;
  onClick?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  onRemove?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  testid?: string;
};

export const TagCollection: FC<TagCollectionProps> = ({
  collection,
  onClick,
  onRemove,
  label,
  testid,
}) => (
  <Tag
    label={label}
    value={shortenTagText(collection.join(', '), 25)}
    onClick={onClick}
    onRemove={onRemove}
    testid={testid}
  />
);

type TagRangeProps = {
  min: number | string | null;
  max: number | string | null;
  label: ReactNode;
  onClick?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  onRemove?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  testid?: string;
};

export const TagRange: FC<TagRangeProps> = ({
  min,
  max,
  onClick,
  onRemove,
  label,
  testid,
}) =>
  min === null && max === null ? null : (
    <Tag
      label={label}
      value={
        <>
          {min !== null && max !== null && min !== max
            ? `${shortenTagText(min)} - ${shortenTagText(max)}`
            : min !== null &&
              max !== null &&
              min === max &&
              `equals ${shortenTagText(min)}`}
          {min !== null && max === null && `min. ${shortenTagText(min)}`}
          {min === null && max !== null && `max. ${shortenTagText(max)}`}
        </>
      }
      onClick={onClick}
      onRemove={onRemove}
      testid={testid}
    />
  );
